/**
 * HubDetail — single-document detail view inside the Hub.
 *
 * The pane the writer uses to:
 *   1. Confirm/change the source doctype.
 *   2. Edit per-doc tag values (substance_name, batch_id, stage,
 *      etc. — driven by Spec 32's source_subtype_tags schema).
 *   3. Read the LLM rationale for the classification (collapsed
 *      by default; the harness writes one, this view surfaces it).
 *   4. Audit who/when uploaded + auto-classified + verified.
 *   5. Verify & save — flips ``verified=true``. Per Spec 34 DR-5,
 *      a subsequent tag edit drops sign-off and forces re-verify.
 *
 * Back is owned by the slide-over header (the back-stack lives
 * there so the writer's drill path library-list → list → detail is
 * coherent). HubDetail's ``onBack`` prop is preserved — the slide-
 * over wires it to the Skip button + the verify-and-leave flow —
 * but HubDetail no longer renders a top-bar back button itself.
 *
 * Inline error banner when ``source_classification.error`` is set
 * (the Aragen-style upstream 5xx case from the pilot — surfaces
 * the failure for needs-review without confusing the writer about
 * doctype state).
 *
 * The full doctype taxonomy + per-key enum values come from M1b's
 * ``/api/library/{id}/doctype-taxonomy`` endpoint (blocked on Spec
 * 32 MR !21 — see Spec 34 milestones). For M6, tag VALUE edits are
 * free-text; once M1b lands, enum-bound keys will render dropdowns
 * (HubDetail's tag-row branch is structured to plug in cleanly).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { aiColors } from "../tokens";
import type { DocumentRow } from "../types";
import { AIReadinessChip } from "./AIReadinessChip";
import { ClassificationIntelPanel } from "./ClassificationIntelPanel";
import { ConfidenceBar } from "./ConfidenceBar";
import { TagQualityRing } from "./TagQualityRing";

export interface HubDetailProps {
  row: DocumentRow;
  /** Returns the slide-over to ``list`` mode. The slide-over header
   *  owns the visible back button; this prop is also wired to the
   *  Skip footer button so a writer who wants to leave without
   *  verifying still has a one-click exit. */
  onBack: () => void;
  /** "✓ Verify & save" — saves edits + flips ``verified=true``.
   *  Parent owns the API call; HubDetail just emits the new tag
   *  map, the (possibly changed) source_doctype, and the implicit
   *  verify intent. */
  onVerify: (input: {
    doc_id: string;
    tags: Record<string, string | null>;
    source_doctype?: string;
  }) => void;
  /** Available source doctype options for the change dropdown.
   *  Derived from the library's target_doctype; always includes "Other".
   *  Defaults to a minimal fallback when not provided. */
  docTypeOptions?: readonly string[];
  /** True while a verify call is in flight. */
  saving?: boolean;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
    minHeight: 0,
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: "0 14px 12px",
  },
  // Sticky header inside the scroll body — fileHeader + doctypePanel
  // stay pinned to the top of the visible viewport as the writer
  // scrolls through tags and audit history.
  stickyHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: surfaceColors.paper,
    zIndex: 2,
    paddingTop: "12px",
    paddingBottom: "6px",
    // Extend the background to the edges so no content peeks
    // behind the sticky element when it clips the padded body.
    marginLeft: "-14px",
    marginRight: "-14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    marginBottom: "10px",
  },
  fileHeader: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    marginBottom: "10px",
  },
  fileBadge: {
    width: "36px",
    height: "44px",
    borderRadius: "3px",
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.mid}`,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "4px",
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: brandColors.primary,
    flexShrink: 0,
  },
  fileName: {
    fontSize: "13px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
    marginBottom: "4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "220px",
    lineHeight: 1.3,
    cursor: "help",
  },
  fileMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  statusSection: {
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: `1px solid ${surfaceColors.line}`,
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
  },
  doctypePanel: {
    backgroundColor: brandColors.light,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "8px 10px",
    marginBottom: "10px",
  },
  doctypeHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    marginBottom: "5px",
  },
  doctypeLabel: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  doctypeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    backgroundColor: "white",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
  },
  doctypeValue: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink,
  },
  errorBanner: {
    marginTop: "8px",
    padding: "6px 8px",
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    borderRadius: "3px",
    fontSize: "10px",
    color: statusColors.danger,
    fontFamily: fonts.body,
  },
  warningRow: {
    marginTop: "6px",
    fontSize: "10px",
    color: "#7C2D12",
    display: "flex",
    alignItems: "flex-start",
    columnGap: "6px",
  },
  warningDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    backgroundColor: statusColors.danger,
    marginTop: "4px",
    flexShrink: 0,
  },
  rationaleToggle: {
    marginTop: "8px",
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    ":hover": { color: brandColors.primary },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  rationaleBody: {
    marginTop: "6px",
    padding: "8px 10px",
    backgroundColor: surfaceColors.paperDim,
    borderRadius: "3px",
    fontSize: "11px",
    color: inkColors.ink2,
    fontFamily: fonts.body,
    lineHeight: 1.5,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "10px",
    marginBottom: "6px",
  },
  sectionLabel: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tagList: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  tagRow: {
    display: "grid",
    gridTemplateColumns: "110px 1fr 40px",
    columnGap: "6px",
    alignItems: "center",
    padding: "6px 8px",
    backgroundColor: "white",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
  },
  tagKey: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  tagValueInput: {
    fontSize: "11px",
    color: inkColors.ink,
    fontFamily: fonts.body,
    padding: "2px 4px",
    border: "1px solid transparent",
    borderRadius: "2px",
    backgroundColor: "transparent",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    ":focus": {
      border: `1px solid ${brandColors.primary}`,
      backgroundColor: "white",
      outline: "none",
    },
  },
  tagValueMissing: {
    color: statusColors.danger,
    fontStyle: "italic",
  },
  tagConfidence: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textAlign: "right",
  },
  emptyTags: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontStyle: "italic",
    padding: "8px",
    backgroundColor: surfaceColors.paperDim,
    border: `1px dashed ${surfaceColors.lineStrong}`,
    borderRadius: "3px",
  },
  auditPanel: {
    marginTop: "14px",
    padding: "10px 12px",
    backgroundColor: surfaceColors.paperDim,
    borderRadius: "3px",
    border: `1px solid ${surfaceColors.line}`,
  },
  auditHeader: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  auditEntries: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    lineHeight: 1.6,
  },
  auditAccent: {
    color: brandColors.primary,
  },
  auditVerified: {
    color: "#0F5132",
    fontWeight: 700,
  },
  labelParamsList: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  labelParamRow: {
    display: "grid",
    gridTemplateColumns: "110px 1fr",
    columnGap: "6px",
    alignItems: "center",
    padding: "6px 8px",
    backgroundColor: "white",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
  },
  labelParamKey: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  labelParamValue: {
    fontSize: "11px",
    color: inkColors.ink,
    fontFamily: fonts.body,
  },
  labelParamNull: {
    fontSize: "11px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    fontStyle: "italic",
  },
  footer: {
    padding: "10px 14px",
    borderTop: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paper,
    flexShrink: 0,
    display: "flex",
    columnGap: "6px",
  },
  verifyBtn: {
    flex: 1,
    backgroundColor: brandColors.primary,
    color: "white",
    border: "none",
    borderRadius: "4px",
    padding: "7px",
    fontSize: "10.5px",
    fontWeight: 700,
    fontFamily: fonts.body,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    cursor: "pointer",
    ":hover": { opacity: 0.9 },
    ":disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  skipBtn: {
    backgroundColor: "transparent",
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "7px 12px",
    fontSize: "10.5px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
  },
  verifiedFooter: {
    flex: 1,
    fontSize: "10px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    alignSelf: "center",
  },
  // ── Phase 2: AI evidence timeline ─────────────────────────
  timeline: {
    marginTop: "14px",
    padding: "10px 12px",
    backgroundColor: aiColors.annotationBg,
    border: `1px solid ${aiColors.annotationBorder}`,
    borderRadius: "4px",
  },
  timelineHeader: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "8px",
  },
  timelineEntries: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    paddingLeft: "6px",
    borderLeft: `2px solid ${aiColors.annotationBorder}`,
  },
  timelineEntry: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
  },
  timelineDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: "1px",
    marginLeft: "-10px",
    border: "2px solid transparent",
  },
  timelineDotDone: {
    backgroundColor: aiColors.evidenceHigh,
    border: `2px solid ${aiColors.evidenceHigh}`,
  },
  timelineDotActive: {
    backgroundColor: "white",
    border: `2px solid ${aiColors.thinking}`,
  },
  timelineDotPending: {
    backgroundColor: "white",
    border: `2px solid ${surfaceColors.lineStrong}`,
  },
  timelineContent: {
    flex: 1,
    minWidth: 0,
  },
  timelineLabel: {
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink2,
    lineHeight: 1.3,
  },
  timelineMeta: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    lineHeight: 1.4,
    marginTop: "1px",
  },
});

function fileExtensionUpper(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1 || idx === filename.length - 1) return "FILE";
  return filename.slice(idx + 1).toUpperCase();
}

function truncateFilename(filename: string, maxLength = 40): string {
  if (filename.length <= maxLength) return filename;
  return `${filename.slice(0, maxLength)}…`;
}

function formatTagValue(value: string | null): string {
  if (value === null || value === "") return "";
  return value;
}

const _FALLBACK_DOC_TYPE_OPTIONS: readonly string[] = ["Other"];

export const HubDetail: React.FC<HubDetailProps> = ({
  row,
  onBack,
  onVerify,
  docTypeOptions = _FALLBACK_DOC_TYPE_OPTIONS,
  saving = false,
}) => {
  const styles = useStyles();

  // Local edit buffer for tag values. Keys come from the source
  // classification; the writer can change values inline. Per Spec
  // 34 DR-5: editing any value drops sign-off — verified is the
  // ROW's flag, but the parent's verify call resets the local
  // ``edited`` mirror. We track ``edited`` purely for the
  // verified-banner messaging.
  const initialTags = useMemo(
    () => ({ ...(row.source_classification?.tags ?? {}) }),
    [row.source_classification?.tags],
  );
  const [tags, setTags] = useState<Record<string, string | null>>(initialTags);
  const [edited, setEdited] = useState(false);

  // Local state for source_doctype — lets the writer override the
  // backend-assigned label before verifying.
  const initialSourceDoctype = row.source_classification?.source_doctype ?? row.doc_type ?? "Other";
  const [localSourceDoctype, setLocalSourceDoctype] = useState<string>(initialSourceDoctype);

  // Reset local state when the row identity changes.
  useEffect(() => {
    void row.doc_id;
    setTags(initialTags);
    setEdited(false);
    setLocalSourceDoctype(row.source_classification?.source_doctype ?? row.doc_type ?? "Other");
  }, [row.doc_id, initialTags, row.source_classification?.source_doctype, row.doc_type]);

  const handleTagChange = (key: string, value: string): void => {
    setTags((prev) => ({ ...prev, [key]: value }));
    setEdited(true);
  };

  const handleVerify = (): void => {
    if (!row.doc_id) return;
    onVerify({ doc_id: row.doc_id, tags, source_doctype: localSourceDoctype });
    setEdited(false);
  };

  const sourceClassification = row.source_classification;
  const tagEntries = Object.entries(tags);
  const conf = sourceClassification?.source_doctype_confidence ?? null;
  const isVerifiedAndUnedited = row.verified && !edited;

  return (
    <div className={styles.root} data-testid="hub-detail">
      <div className={styles.body}>
        <div className={styles.stickyHeader}>
          <div className={styles.fileHeader}>
            <div className={styles.fileBadge} aria-hidden="true">
              {fileExtensionUpper(row.filename)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={styles.fileName} title={row.filename}>
                {truncateFilename(row.filename)}
              </div>
              <div className={styles.fileMeta}>
                {row.upstream_status ?? "—"}
                {row.parsed === true && " · parsed"}
                {row.parsed === false && " · not parsed"}
              </div>

              {/* Status section — AI Readiness badge below filename */}
              <div className={styles.statusSection}>
                <AIReadinessChip row={row} variant="full" />
              </div>
            </div>
          </div>

          <ClassificationIntelPanel
            row={row}
            localSourceDoctype={localSourceDoctype}
            docTypeOptions={docTypeOptions}
            onDoctypeChange={(dt) => {
              setLocalSourceDoctype(dt);
              setEdited(true);
            }}
          />
        </div>
        {/* end stickyHeader */}

        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Tags</span>
          {(() => {
            const tagTotal = Object.keys(tags).length;
            const tagFilled = Object.values(tags).filter((v) => v !== null).length;
            return tagTotal > 0 ? <TagQualityRing filled={tagFilled} total={tagTotal} size={20} /> : null;
          })()}
        </div>

        {tagEntries.length === 0 ? (
          <div className={styles.emptyTags}>No tags yet — pick a doctype above to populate.</div>
        ) : (
          <div className={styles.tagList}>
            {tagEntries.map(([key, value]) => {
              const formatted = formatTagValue(value);
              const tagConfidence = sourceClassification?.tag_confidences[key];
              return (
                <div key={key} className={styles.tagRow}>
                  <span className={styles.tagKey}>{key}</span>
                  <input
                    type="text"
                    value={formatted}
                    onChange={(e) => handleTagChange(key, e.target.value)}
                    placeholder={value === null ? "missing — please add" : ""}
                    className={mergeClasses(styles.tagValueInput, value === null && styles.tagValueMissing)}
                    aria-label={`Edit value for tag ${key}`}
                    data-testid={`hub-detail-tag-${key}`}
                  />
                  <div className={styles.tagConfidence}>
                    {typeof tagConfidence === "number" ? (
                      <ConfidenceBar confidence={tagConfidence} size="sm" showLabel />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {row.label_params && Object.keys(row.label_params).length > 0 && (
          <>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionLabel}>Extracted parameters</span>
            </div>
            <div className={styles.labelParamsList} data-testid="hub-detail-label-params">
              {Object.entries(row.label_params).map(([key, value]) => (
                <div key={key} className={styles.labelParamRow}>
                  <span className={styles.labelParamKey}>{key}</span>
                  {value !== null ? (
                    <span className={styles.labelParamValue}>{value}</span>
                  ) : (
                    <span className={styles.labelParamNull}>—</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className={styles.timeline}>
          <div className={styles.timelineHeader}>◆ Evidence history</div>
          <div className={styles.timelineEntries}>
            {/* Step 1: Uploaded — always shown */}
            <div className={styles.timelineEntry}>
              <span className={mergeClasses(styles.timelineDot, styles.timelineDotDone)} aria-hidden="true" />
              <div className={styles.timelineContent}>
                <div className={styles.timelineLabel}>Uploaded</div>
                {row.upstream_status && <div className={styles.timelineMeta}>{row.upstream_status}</div>}
              </div>
            </div>

            {/* Step 2: Text extraction */}
            {row.parsed !== null && row.parsed !== undefined && (
              <div className={styles.timelineEntry}>
                <span
                  className={mergeClasses(
                    styles.timelineDot,
                    row.parsed ? styles.timelineDotDone : styles.timelineDotPending,
                  )}
                  aria-hidden="true"
                />
                <div className={styles.timelineContent}>
                  <div className={styles.timelineLabel}>{row.parsed ? "Text extracted" : "Extraction incomplete"}</div>
                  {!row.parsed && <div className={styles.timelineMeta}>limited text coverage</div>}
                </div>
              </div>
            )}

            {/* Step 3: AI classification */}
            {sourceClassification?.source_doctype && (
              <div className={styles.timelineEntry}>
                <span className={mergeClasses(styles.timelineDot, styles.timelineDotDone)} aria-hidden="true" />
                <div className={styles.timelineContent}>
                  <div className={styles.timelineLabel}>
                    AI classified{" "}
                    <span style={{ color: aiColors.thinking }}>{sourceClassification.source_doctype}</span>
                  </div>
                  {typeof conf === "number" && (
                    <div className={styles.timelineMeta}>{Math.round(conf * 100)}% confidence</div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Verified or awaiting */}
            {row.verified && row.verified_by ? (
              <div className={styles.timelineEntry}>
                <span className={mergeClasses(styles.timelineDot, styles.timelineDotDone)} aria-hidden="true" />
                <div className={styles.timelineContent}>
                  <div className={styles.timelineLabel} style={{ color: aiColors.trustVerified }}>
                    Evidence signed off
                  </div>
                  <div className={styles.timelineMeta}>
                    by {row.verified_by}
                    {row.verified_at && ` · ${row.verified_at}`}
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.timelineEntry}>
                <span className={mergeClasses(styles.timelineDot, styles.timelineDotPending)} aria-hidden="true" />
                <div className={styles.timelineContent}>
                  <div className={styles.timelineLabel} style={{ color: inkColors.ink4 }}>
                    Awaiting sign-off
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Pending edits after verification */}
            {edited && row.verified && (
              <div className={styles.timelineEntry}>
                <span className={mergeClasses(styles.timelineDot, styles.timelineDotActive)} aria-hidden="true" />
                <div className={styles.timelineContent}>
                  <div className={styles.timelineLabel} style={{ color: statusColors.warn }}>
                    Edits pending
                  </div>
                  <div className={styles.timelineMeta}>re-verify to restore sign-off</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        {!isVerifiedAndUnedited ? (
          <>
            <button
              type="button"
              className={styles.verifyBtn}
              onClick={handleVerify}
              disabled={saving || !row.doc_id}
              data-testid="hub-detail-verify"
            >
              {saving ? "Saving…" : "✓ Verify & save"}
            </button>
            <button type="button" className={styles.skipBtn} onClick={onBack} data-testid="hub-detail-skip">
              Skip
            </button>
          </>
        ) : (
          <span className={styles.verifiedFooter} data-testid="hub-detail-verified-hint">
            ✓ Evidence signed off — edits require re-verification
          </span>
        )}
      </div>
    </div>
  );
};

export default HubDetail;
